"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "motion/react";

import { cn } from "../lib/cn";
import { transitions } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Avatar, type AvatarProps } from "./Avatar";

/**
 * AttributionChip — small floating chip that attributes an inline edit /
 * comment / suggestion to a user. Composes the Avatar primitive on the left,
 * a name + timestamp pair in the middle, and a slot for inline actions
 * (accept/reject mini-buttons, "view comment", etc.) on the right.
 *
 * Positioning is consumer-owned. The chip is `position: absolute` by default
 * so the consumer can wrap it in a relatively-positioned anchor and offset it
 * from the edited region with `top` / `left`. Pass `placement="inline"` to
 * drop the absolute positioning and use it as a regular flow element (e.g.
 * inside a sidebar list of recent edits).
 *
 * Tones: `default` (neutral, human-authored edits) and `ai` (AI-suggestion
 * source, tinted via the existing `--accent` token). The diff-tinted `ins` /
 * `del` tones from pretty are intentionally not ported here — they need a
 * separate diff-color token family that the redline-tokens work owns.
 *
 * Mount animation: a short fade + slide-up via `motion/react` using the
 * `transitions.surface` preset (240ms, smooth-out). Honors
 * `prefers-reduced-motion` via `useReducedMotion()` — reduced-motion users get
 * a static, non-animated render. Pass `motion={false}` to opt out everywhere.
 */
const attributionChipVariants = cva(
  [
    "inline-flex items-center gap-2 rounded-full border bg-card/95 backdrop-blur-sm",
    "px-2 py-1 text-xs shadow-sm",
    "border-border text-foreground",
  ].join(" "),
  {
    variants: {
      tone: {
        // Default neutral chip — for human-authored edits.
        default: "",
        // AI-suggestion chip — for chips that mark an AI suggestion's source.
        // Resolves via the existing --accent token (no new token family).
        ai: "border-accent/30 bg-accent/10",
      },
      size: {
        sm: "px-1.5 py-0.5 text-[11px]",
        default: "px-2 py-1 text-xs",
      },
      placement: {
        // Floating overlay (default) — consumer wraps in a relative parent and
        // positions with top/left/transform.
        floating: "absolute z-10",
        // Inline flow — drops absolute positioning. Use inside lists / sidebars.
        inline: "relative",
      },
    },
    defaultVariants: { tone: "default", size: "default", placement: "floating" },
  },
);

export interface AttributionChipProps
  extends Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "title" | "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
    >,
    VariantProps<typeof attributionChipVariants> {
  /** User's display name. Required for a11y label. */
  name: string;
  /** Avatar image. Falls back to initials of `name`. */
  avatarUrl?: string | null;
  /**
   * ISO timestamp shown next to the name. Pass an already-formatted string via
   * `formattedTimestamp` to override the default short form ("12m ago").
   */
  timestamp?: string;
  /** Already-formatted timestamp; bypasses the ISO formatter. */
  formattedTimestamp?: string;
  /** Right-side slot for accept/reject/view actions. */
  actions?: React.ReactNode;
  /** Compact mode — drops the timestamp, keeps avatar + name + actions. */
  compact?: boolean;
  /** Disable the mount animation. Default true; reduced-motion forces static. */
  motion?: boolean;
  /**
   * Avatar shape. Defaults to `circle`; pass `rounded` to match consumer
   * surfaces that use rounded-2xl monogram tiles.
   */
  avatarShape?: AvatarProps["shape"];
}

/**
 * Default ISO-to-short formatter. Falls back to the raw string if `Date.parse`
 * can't parse it. Internal helper so consumers don't have to wire date-fns
 * just to get "12m ago".
 */
function formatRelative(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const diffSec = Math.round((Date.now() - ms) / 1000);
  if (diffSec < 45) return "just now";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86_400)}d ago`;
}

export const AttributionChip = React.forwardRef<HTMLDivElement, AttributionChipProps>(
  (
    {
      className,
      tone,
      size,
      placement,
      name,
      avatarUrl,
      timestamp,
      formattedTimestamp,
      actions,
      compact = false,
      motion: animateProp = true,
      avatarShape = "circle",
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    // Treat `null` (pre-resolve) as "motion allowed" so SSR content does not
    // animate-then-snap once the client resolves, per the hook's guidance.
    const motionAllowed = reduced === false || reduced === null;
    const animated = animateProp && motionAllowed;

    const display = formattedTimestamp ?? (timestamp ? formatRelative(timestamp) : null);
    const classes = cn(attributionChipVariants({ tone, size, placement }), className);

    const inner = (
      <>
        <Avatar
          src={avatarUrl ?? undefined}
          name={name}
          size="xs"
          shape={avatarShape}
          colorFromName
        />
        <span className="font-medium leading-none">{name}</span>
        {!compact && display ? (
          <span className="leading-none text-muted-foreground">{display}</span>
        ) : null}
        {actions ? (
          <span className="ml-1 inline-flex items-center gap-1">{actions}</span>
        ) : null}
      </>
    );

    if (animated) {
      return (
        <motion.div
          ref={ref}
          role="group"
          aria-label={`Edit by ${name}`}
          className={classes}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={transitions.surface}
          {...props}
        >
          {inner}
        </motion.div>
      );
    }

    return (
      <div ref={ref} role="group" aria-label={`Edit by ${name}`} className={classes} {...props}>
        {inner}
      </div>
    );
  },
);
AttributionChip.displayName = "AttributionChip";

export { attributionChipVariants };
